import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import CryptoImg from 'assets/payment/crypto.png'
import {allIcons} from "components/Finance/utils";
import BottomArrow from '../assets/arrow-down.svg';
import TopArrow from '../assets/arrow-top.svg';

export const StripeIcons: React.FC = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [contentHeight, setContentHeight] = useState(0);
    const contentRef = useRef<HTMLDivElement>(null);
    const [imagesLoaded, setImagesLoaded] = useState(false);

    const measureHeight = () => {
        if (contentRef.current) {
            const currentMaxHeight = contentRef.current.style.maxHeight;
            contentRef.current.style.maxHeight = 'none';

            const height = contentRef.current.scrollHeight;
            setContentHeight(height);

            contentRef.current.style.maxHeight = currentMaxHeight;
        }
    };

    useEffect(() => {
        if (allIcons.length === 0) return;

        let loadedCount = 0;
        const totalImages = allIcons.length;

        const checkAllLoaded = () => {
            loadedCount++;
            if (loadedCount === totalImages) {
                setImagesLoaded(true);

                setTimeout(measureHeight, 50);
            }
        };

        allIcons.forEach(icon => {
            const img = new Image();
            img.onload = checkAllLoaded;
            img.onerror = checkAllLoaded;
            img.src = icon.src;
        });
    }, [allIcons]);

    useEffect(() => {
        if (imagesLoaded) {
            measureHeight();
        }
    }, [imagesLoaded]);

    useEffect(() => {
        const handleResize = () => {
            measureHeight();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleExpand = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    return (
        <IconsContainer>
            <ExpandableWrapper
                ref={contentRef}
                style={{
                    maxHeight: isExpanded ? `${contentHeight}px` : '16px',
                    transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            >
                <IconsWrapper>
                    {allIcons.map((icon, index) => (
                        <IconImage
                            key={index}
                            loading="eager"
                            src={icon.src}
                            alt={icon.alt}
                        />
                    ))}
                </IconsWrapper>
            </ExpandableWrapper>
            <ToggleButton onClick={toggleExpand}>
                {isExpanded ?
                    <>
                        <span>Show Less</span>
                        <div><TopArrow /></div>
                    </>
                    :
                    <>
                        <span>Show More</span>
                        <div>
                            <BottomArrow />
                        </div>
                    </>
                }
            </ToggleButton>
        </IconsContainer>
    );
};

export const CryptoIcons: React.FC = () => (
    <CryptoContainer>
        <img loading="eager" src={CryptoImg} alt="Crypto" />
    </CryptoContainer>
);

const IconsContainer = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    position: relative;

    @media (max-width: 1024px) {
        gap: 5px;
    }

    @media (max-width: 768px) {
        gap: 4px;
    }
`;

const ExpandableWrapper = styled.div`
    overflow: hidden;
    flex: 1;
`;

const IconsWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    @media (max-width: 1024px) {
        gap: 10px;
    }

    @media (max-width: 768px) {
        gap: 8px;
    }
`;

const IconImage = styled.img`
    height: 16px;
    width: auto;
    max-width: 50px;
    object-fit: contain;
    opacity: 1;

    @media (max-width: 768px) {
        height: 14px;
        max-width: 45px;
    }
`;

const ToggleButton = styled.button`
    display: none;
    width: 100px;

    @media (max-width: 1620px) {
        display: flex;
        gap: 5px;
        align-items: center;
        justify-content: center;
        background: transparent;
        color: #00000080;
        border: none;
        border-radius: 6px;
        padding: 4px 8px;
        font-size: 11px;
        font-weight: 600;
        font-family: SF Pro Display, sans-serif;
        cursor: pointer;
        flex-shrink: 0;
        transition: background 0.2s ease;

        &:active {
            transform: scale(0.95);
        }
    }
`;

const CryptoContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    img {
        height: 18px;
        width: auto;
        object-fit: contain;
        opacity: 1;
    }

    @media (max-width: 1024px) {
        padding-left: 20px;
    }

    @media (max-width: 768px) {
        padding-left: 0;

        img {
            height: 16px;
        }
    }
`;